import { brandRamp } from '@/shared/utils/brandRamp'
import {
  clearBrandCssCache,
  readCachedBrandCss,
  workspaceSlugFromPath,
  writeCachedBrandHex,
} from '@/shared/utils/brandRampCache'
import { BRAND_ATTR, BRAND_STYLE_ID, brandRampCss } from '@/shared/utils/brandRampCss'
import { workspaceStore } from '@/stores/workspace.store'
import { useLocation } from '@solidjs/router'
import { createEffect, createMemo, onCleanup } from 'solid-js'

const HIRING_MANAGER_PREFIX = '/app/hiring-manager/'

function isBrandableRoute(pathname: string): boolean {
  return workspaceSlugFromPath(pathname) !== null || pathname.startsWith(HIRING_MANAGER_PREFIX)
}

function ensureStyleElement(): HTMLStyleElement {
  const existing = document.getElementById(BRAND_STYLE_ID)
  if (existing instanceof HTMLStyleElement) {
    return existing
  }
  existing?.remove()
  const style = document.createElement('style')
  style.id = BRAND_STYLE_ID
  document.head.appendChild(style)
  return style
}

function applyBrandCss(css: string): void {
  const style = ensureStyleElement()
  if (style.textContent !== css) {
    style.textContent = css
  }
  document.documentElement.setAttribute(BRAND_ATTR, '')
}

function removeBrandCss(): void {
  document.documentElement.removeAttribute(BRAND_ATTR)
  document.getElementById(BRAND_STYLE_ID)?.remove()
}

export function useWorkspaceBrandTheme(): void {
  const location = useLocation()

  const loadedSlug = createMemo(() => workspaceStore.workspace?.slug ?? null)

  const liveHex = createMemo(() => {
    const branding = workspaceStore.workspace?.branding
    if (!branding?.apply_to_app || !branding.brand_color) {
      return null
    }
    return branding.brand_color
  })

  const liveCss = createMemo(() => {
    const hex = liveHex()
    if (hex === null) {
      return null
    }
    const ramp = brandRamp(hex)
    return ramp === null ? null : brandRampCss(ramp)
  })

  createEffect(() => {
    const pathname = location.pathname

    if (!isBrandableRoute(pathname)) {
      removeBrandCss()
      return
    }

    const urlSlug = workspaceSlugFromPath(pathname)
    const storeSlug = loadedSlug()

    if (storeSlug !== null && (urlSlug === null || urlSlug === storeSlug)) {
      const css = liveCss()

      if (css !== null) {
        const hex = liveHex()
        if (urlSlug !== null && hex !== null) {
          writeCachedBrandHex(urlSlug, hex)
        }
        applyBrandCss(css)
        return
      }

      if (urlSlug !== null && readCachedBrandCss(urlSlug) !== null) {
        clearBrandCssCache()
      }
      removeBrandCss()
      return
    }

    const cached = urlSlug === null ? null : readCachedBrandCss(urlSlug)
    if (cached !== null) {
      applyBrandCss(cached)
      return
    }

    removeBrandCss()
  })

  onCleanup(removeBrandCss)
}
